import { useState, useMemo } from "react";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Eye, Download, ShoppingBag, ArrowLeft, Package, ChevronLeft, ChevronRight, XCircle, Filter, Search, X, FileText } from "lucide-react";
import { useDateFormat } from '@/contexts/DateFormatContext';
import { useLocation } from "wouter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Mail } from "lucide-react";

export default function Orders() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [selectedOrder, setSelectedOrder] = useState<number | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const { formatDate, formatDateTime } = useDateFormat();
  const utils = trpc.useContext();

  const resendEmailMutation = trpc.orders.resendPaymentEmail.useMutation({
    onSuccess: () => {
      toast.success("E-Mail erneut gesendet.");
    },
    onError: (error) => {
      toast.error(error.message || "E-Mail-Versand fehlgeschlagen");
    },
  });

  const cancelOrderMutation = trpc.orders.cancelOrder.useMutation({
    onSuccess: () => {
      toast.success(t.orders.orderCancelled);
      utils.orders.list.invalidate();
      setCancelOrderId(null);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDownloadInvoice = (orderId: number) => {
    const url = `/api/orders/${orderId}/invoice`;
    const a = document.createElement('a');
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(t.orders.invoiceDownloaded);
  };

  // Fetch projects for filter dropdown
  const { data: projectsData } = trpc.projects.list.useQuery(undefined, { staleTime: 60_000 });

  // Debounce search (300ms)
  useMemo(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Server-side paginated query
  const { data: paginatedData, isLoading } = trpc.orders.list.useQuery({
    page,
    pageSize,
    status: statusFilter !== "all" ? statusFilter : undefined,
    search: debouncedSearch || undefined,
    projectId: projectFilter !== "all"
      ? projectsData?.find(p => p.title === projectFilter)?.id
      : undefined,
  });

  const paginatedOrders = paginatedData?.orders || [];
  const totalPages = paginatedData?.totalPages || 1;
  const totalOrders = paginatedData?.total || 0;
  
  // Project titles for dropdown
  const uniqueProjects: string[] = Array.from(new Set((projectsData || []).map(p => p.title))).sort();
  
  // Reset to page 1 when filters change
  const handleFilterChange = () => {
    setPage(1);
  };
  const { data: orderDetails } = trpc.orders.getById.useQuery(
    { id: selectedOrder || 0 },
    { enabled: !!selectedOrder }
  );

  const handleViewDetails = (orderId: number) => {
    navigate(`/orders/${orderId}`);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      paid: "default",
      failed: "destructive",
      refunded: "outline",
      shipped: "default",
      cancelled: "destructive",
    };

    const labels: Record<string, string> = {
      pending: t.orders.pending,
      paid: t.orders.paid,
      failed: t.orders.failed,
      refunded: t.orders.refunded,
      shipped: t.orders.shipped,
      cancelled: t.orders.cancelled,
    };

    return (
      <Badge variant={variants[status] || "default"}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900 p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">{t.common.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900">
      <div className="container mx-auto p-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate("/")}
          className="mb-6 gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.common.back}
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold flex items-center gap-3">
            <ShoppingBag className="h-9 w-9 text-purple-600" />
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {t.orders.title}
            </span>
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            {t.orders.totalOrders}: {totalOrders}
            {statusFilter !== "all" && (
              <span className="ml-2 text-sm text-purple-600 dark:text-purple-400">
                ({t.orders.filtered || "filtered"})
              </span>
            )}
            {totalPages > 1 && (
              <span className="ml-2 text-sm">
                ({t.common.page || "Page"} {page} {t.common.of || "of"} {totalPages})
              </span>
            )}
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6 backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5 text-purple-600" />
              {t.orders.filters || "Filters"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t.orders.searchPlaceholder || "Search by order number, name, or email..."}
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); handleFilterChange(); }}
                    className="pl-9"
                  />
                </div>
              </div>
              
              {/* Status Filter */}
              <div className="w-full md:w-48">
                <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); handleFilterChange(); }}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.orders.allStatuses || "All statuses"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.orders.allStatuses || "All statuses"}</SelectItem>
                    <SelectItem value="pending">{t.orders.pending}</SelectItem>
                    <SelectItem value="paid">{t.orders.paid}</SelectItem>
                    <SelectItem value="shipped">{t.orders.shipped}</SelectItem>
                    <SelectItem value="cancelled">{t.orders.cancelled}</SelectItem>
                    <SelectItem value="failed">{t.orders.failed}</SelectItem>
                    <SelectItem value="refunded">{t.orders.refunded}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Project Filter */}
              <div className="w-full md:w-48">
                <Select value={projectFilter} onValueChange={(value) => { setProjectFilter(value); handleFilterChange(); }}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.orders.allProjects || "All projects"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.orders.allProjects || "All projects"}</SelectItem>
                    {uniqueProjects.map(project => (
                      <SelectItem key={project} value={project}>{project}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Clear Filters */}
              {(statusFilter !== "all" || projectFilter !== "all" || searchQuery) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setStatusFilter("all");
                    setProjectFilter("all");
                    setSearchQuery("");
                    setDebouncedSearch("");
                    setPage(1);
                  }}
                  className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                >
                  <X className="h-4 w-4" />
                  {t.orders.clearFilters || "Clear"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        {paginatedOrders.length === 0 ? (
          <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="rounded-full bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 p-6 mb-4">
                <ShoppingBag className="h-16 w-16 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-muted-foreground text-lg">{t.orders.noOrders}</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <ShoppingBag className="h-6 w-6 text-purple-600" />
                {t.orders.title}
              </CardTitle>
              <CardDescription className="text-base">
                {t.orders.ordersDescription}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg overflow-hidden border border-purple-100 dark:border-purple-900">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 hover:from-purple-100 hover:to-pink-100 dark:hover:from-purple-900/30 dark:hover:to-pink-900/30">
                      <TableHead className="font-semibold">{t.orders.orderNumber}</TableHead>
                      <TableHead className="font-semibold">{t.orders.project}</TableHead>
                      <TableHead className="font-semibold">{t.orders.customerName}</TableHead>
                      <TableHead className="font-semibold">{t.orders.customerEmail}</TableHead>
                      <TableHead className="font-semibold">{t.orders.totalAmount}</TableHead>
                      <TableHead className="font-semibold">{t.orders.orderStatus}</TableHead>
                      <TableHead className="font-semibold">{t.orders.orderDate}</TableHead>
                      <TableHead className="font-semibold">{t.common.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedOrders.map((order) => (
                      <TableRow 
                        key={order.id}
                        className="hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-colors"
                      >
                        <TableCell className="font-mono font-medium">
                          <div className="flex items-center gap-2 flex-wrap">
                            {order.orderNumber}
                              {order.requiresShipping && (order as any).deliveryType === "delivery" && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
                                <Package className="h-3 w-3 mr-1" />
                                  {t.orders.delivery || "Lieferung"}
                                </Badge>
                              )}
                              {order.requiresShipping && (order as any).deliveryType === "pickup" && (
                                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700">
                                  <Package className="h-3 w-3 mr-1" />
                                  {t.orders.pickup || "Abholung"}
                              </Badge>
                            )}
                            {(order as any).downloadedAt && (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">
                                <Download className="h-3 w-3 mr-1" />
                                {t.orders.downloaded || "Downloaded"}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{order.projectTitle}</TableCell>
                        <TableCell>{order.customerName || "-"}</TableCell>
                        <TableCell>{order.customerEmail || "-"}</TableCell>
                        <TableCell className="font-semibold">
                          {parseFloat(order.totalAmount).toFixed(2)} {order.currency}
                        </TableCell>
                        <TableCell>{getStatusBadge(order.status)}</TableCell>
                        <TableCell>
                          {formatDate(order.createdAt)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetails(order.id)}
                              className="hover:bg-purple-100 hover:text-purple-700 hover:border-purple-300 dark:hover:bg-purple-900/30 dark:hover:text-purple-300 transition-all"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              {t.orders.viewOrder}
                            </Button>
                            {(order as any).invoicePdfKey && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadInvoice(order.id)}
                                className="hover:bg-green-100 hover:text-green-700 hover:border-green-300 dark:hover:bg-green-900/30 dark:hover:text-green-300 transition-all"
                                title={t.orders.downloadInvoice}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                            {order.requiresShipping && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/orders/${order.id}/shipping`)}
                                className="hover:bg-blue-100 hover:text-blue-700 hover:border-blue-300 dark:hover:bg-blue-900/30 dark:hover:text-blue-300 transition-all"
                              >
                                <Package className="h-4 w-4" />
                              </Button>
                            )}
                            {!order.requiresShipping && order.status === 'paid' && order.customerEmail && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => resendEmailMutation.mutate({ orderId: order.id })}
                                className="hover:bg-yellow-100 hover:text-yellow-700 hover:border-yellow-300 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-300 transition-all"
                                title="E-Mail erneut senden"
                              >
                                <Mail className="h-4 w-4 mr-1" />
                                Resend Email
                              </Button>
                            )}
                            {order.status !== 'cancelled' && order.status !== 'shipped' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCancelOrderId(order.id)}
                                className="hover:bg-red-100 hover:text-red-700 hover:border-red-300 dark:hover:bg-red-900/30 dark:hover:text-red-300 transition-all"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-purple-200 dark:border-purple-800">
                  <div className="text-sm text-muted-foreground">
                    {t.common.showing || "Showing"} {Math.max(0, (page - 1) * pageSize + 1)} - {Math.min(page * pageSize, totalOrders)} {t.common.of || "of"} {totalOrders}
                  </div>
                  <div className="flex gap-2 items-center">
                    {/* Page size selector */}
                    <Select value={String(pageSize)} onValueChange={(val) => { setPageSize(Number(val)); setPage(1); }}>
                      <SelectTrigger className="w-24">
                        <SelectValue placeholder="20" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t.common.previous}
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setPage(pageNum)}
                            className={page === pageNum ? "bg-purple-600 hover:bg-purple-700" : ""}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="gap-1"
                    >
                      {t.common.next}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-3xl backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
            <DialogHeader>
              <DialogTitle className="text-2xl">{t.orders.orderDetails}</DialogTitle>
            </DialogHeader>

            {orderDetails && (
              <div className="space-y-6">
                {/* Order Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{t.orders.orderNumber}</p>
                    <p className="font-mono font-semibold">{orderDetails.orderNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t.orders.orderStatus}</p>
                    {getStatusBadge(orderDetails.status)}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t.orders.customerName}</p>
                    <p className="font-medium">{orderDetails.customerName || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t.orders.customerEmail}</p>
                    <p className="font-medium">{orderDetails.customerEmail || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t.orders.totalAmount}</p>
                    <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                      {parseFloat(orderDetails.totalAmount).toFixed(2)} {orderDetails.currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t.orders.orderDate}</p>
                    <p className="font-medium">{formatDateTime(orderDetails.createdAt)}</p>
                  </div>
                </div>

                {/* Payment Info */}
                {orderDetails.payment && (
                  <div>
                    <h3 className="font-semibold mb-2 text-lg">{t.orders.paymentDetails}</h3>
                    <div className="grid grid-cols-2 gap-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                      <div>
                        <p className="text-sm text-muted-foreground">{t.orders.paymentMethod}</p>
                        <p className="capitalize font-medium">{orderDetails.payment.paymentProvider}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t.orders.transactionId}</p>
                        <p className="font-mono text-sm font-medium">{orderDetails.payment.transactionId}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Order Items */}
                <div>
                  <h3 className="font-semibold mb-2 text-lg">{t.orders.items}</h3>
                  <div className="rounded-lg overflow-hidden border border-purple-200 dark:border-purple-800">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                          <TableHead className="font-semibold">{t.orders.image}</TableHead>
                          <TableHead className="font-semibold">{t.common.quantity}</TableHead>
                          <TableHead className="font-semibold">{t.common.price}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orderDetails.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.filename}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell className="font-semibold">
                              {parseFloat(item.price).toFixed(2)} {orderDetails.currency}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Cancel Order Confirmation Dialog */}
        <AlertDialog open={!!cancelOrderId} onOpenChange={(open) => !open && setCancelOrderId(null)}>
          <AlertDialogContent className="backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
            <AlertDialogHeader>
              <AlertDialogTitle>{t.orders.cancelOrder}</AlertDialogTitle>
              <AlertDialogDescription>
                {t.orders.confirmCancel}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t.common.cancel}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => cancelOrderId && cancelOrderMutation.mutate({ orderId: cancelOrderId })}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {t.orders.cancelOrder}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
